import {Component, computed, effect, inject, input, output, signal} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {TranslocoPipe, TranslocoService} from '@jsverse/transloco';
import {Button} from 'primeng/button';
import {Checkbox} from 'primeng/checkbox';
import {Dialog} from 'primeng/dialog';
import {Select} from 'primeng/select';
import {Textarea} from 'primeng/textarea';
import {firstValueFrom} from 'rxjs';
import {
  DirectusService,
  RfxDetailReason,
  RfxMainReason,
  RfxStage,
  RfxTextConnector,
} from '@frontend/directus';
import {BaseParticipatingTool} from '../table/invitation-table/invitation-table.types';
import {
  ParticipatingToolControllerService,
  TenderPhaseType,
  TenderSelection,
} from '@frontend/shared-api';
import {ReasonOption} from './decision-reason-modal.typs';

@Component({
  selector: 'app-decision-reason-modal',
  imports: [FormsModule, TranslocoPipe, Dialog, Button, Checkbox, Select, Textarea],
  templateUrl: './decision-reason-modal.component.html',
})
export class DecisionReasonModalComponent {
  private readonly directusService = inject(DirectusService);
  private readonly transloco = inject(TranslocoService);
  private readonly participatingToolService = inject(ParticipatingToolControllerService);

  readonly visible = input.required<boolean>();
  readonly projectId = input.required<number>();
  readonly phaseType = input<TenderPhaseType>(TenderPhaseType.RFI);
  readonly tool = input<BaseParticipatingTool | null>(null);
  readonly isStepFinished = input<boolean>(false);

  readonly visibleChange = output<boolean>();
  readonly saved = output<unknown>();

  protected readonly loading = signal(false);

  protected readonly mainReasons = signal<RfxMainReason[]>([]);
  protected readonly detailReasons = signal<RfxDetailReason[]>([]);
  private readonly textConnectors = signal<RfxTextConnector[]>([]);
  private readonly selectedConnector = signal<RfxTextConnector | null>(null);

  protected readonly mainReasonId = signal<string | null>(null);
  protected readonly selectedDetailReasonIds = signal<string[]>([]);
  protected readonly generatedText = signal('');
  protected readonly comment = signal('');
  protected readonly canGenerateReasons = signal(false);

  protected readonly mainReasonOptions = computed<ReasonOption[]>(() =>
    this.mainReasons().map((reason) => ({
      label: this.translateMainReasonLabel(reason),
      value: reason.uuid,
    })),
  );

  protected readonly detailReasonOptions = computed<ReasonOption[]>(() =>
    this.detailReasons().map((reason) => ({
      label: this.translateDetailReasonLabel(reason),
      value: reason.uuid,
    })),
  );

  protected readonly canSave = computed(() => !!this.mainReasonId() && !this.isStepFinished());

  protected readonly canGenerate = computed(
    () =>
      !this.isStepFinished() &&
      !this.loading() &&
      this.canGenerateReasons() &&
      !!this.mainReasonId() &&
      this.generatedText().trim().length > 0,
  );

  constructor() {
    effect(() => {
      if (!this.visible()) {
        this.resetDialogState();
        return;
      }

      const reason = this.tool()?.decisionReason;
      this.resetDialogState();
      this.mainReasonId.set(reason?.mainReasonId ?? null);
      this.selectedDetailReasonIds.set(reason?.detailReasonIds ?? []);
      this.generatedText.set(reason?.generatedText ?? '');
      this.comment.set(reason?.comment ?? '');
      this.canGenerateReasons.set(false);

      void this.loadInitialData(reason?.mainReasonId ?? null);
    });
  }

  protected close(): void {
    this.resetDialogState();
    this.visibleChange.emit(false);
  }

  protected async onMainReasonChange(reasonId: string | null): Promise<void> {
    this.mainReasonId.set(reasonId);
    this.selectedDetailReasonIds.set([]);
    this.generatedText.set('');
    this.canGenerateReasons.set(!!reasonId);

    if (!reasonId) {
      this.detailReasons.set([]);
      return;
    }

    await this.loadDetailReasons(reasonId);
    this.rebuildGeneratedText();
  }

  protected onDetailReasonToggle(reasonId: string, checked: boolean): void {
    const current = new Set(this.selectedDetailReasonIds());

    if (checked) {
      current.add(reasonId);
    } else {
      current.delete(reasonId);
    }

    this.selectedDetailReasonIds.set(Array.from(current));
    this.rebuildGeneratedText();
    this.canGenerateReasons.set(true);
  }

  protected onGeneratedTextChange(value: string): void {
    this.generatedText.set(value);
    this.canGenerateReasons.set(true);
  }

  protected onCommentChange(value: string): void {
    this.comment.set(value);
  }

  protected isDetailReasonSelected(reasonId: string): boolean {
    return this.selectedDetailReasonIds().includes(reasonId);
  }

  private async loadInitialData(existingMainReasonId: string | null): Promise<void> {
    try {
      this.loading.set(true);

      const decisionType = this.resolveDecisionType();
      const rfxStage = this.resolveRfxStage();

      const [mainReasons, connectors] = await Promise.all([
        firstValueFrom(
          this.directusService.loadRfxMainReasonsByDecisionType(decisionType, rfxStage),
        ),
        firstValueFrom(this.directusService.loadRfxTextConnectorsByDecisionType(decisionType)),
      ]);

      this.mainReasons.set(mainReasons ?? []);
      this.textConnectors.set(connectors ?? []);
      this.selectedConnector.set(this.pickRandomConnector(connectors ?? []));

      if (existingMainReasonId) {
        await this.loadDetailReasons(existingMainReasonId);
      } else {
        this.detailReasons.set([]);
      }
    } finally {
      this.loading.set(false);
    }
  }

  private async loadDetailReasons(mainReasonId: string): Promise<void> {
    try {
      this.loading.set(true);

      const reasons = await firstValueFrom(
        this.directusService.loadRfxDetailReasonsByMainReason(mainReasonId),
      );

      this.detailReasons.set(reasons ?? []);
    } finally {
      this.loading.set(false);
    }
  }

  private resetDialogState(): void {
    this.loading.set(false);
    this.mainReasons.set([]);
    this.detailReasons.set([]);
    this.textConnectors.set([]);
    this.selectedConnector.set(null);
    this.mainReasonId.set(null);
    this.selectedDetailReasonIds.set([]);
    this.generatedText.set('');
    this.comment.set('');
    this.canGenerateReasons.set(false);
  }

  private pickRandomConnector(connectors: RfxTextConnector[]): RfxTextConnector | null {
    if (!connectors.length) {
      return null;
    }

    const index = Math.floor(Math.random() * connectors.length);
    return connectors[index] ?? null;
  }

  private rebuildGeneratedText(): void {
    const mainReason = this.mainReasons().find((reason) => reason.uuid === this.mainReasonId());
    const detailReasons = this.detailReasons().filter((reason) =>
      this.selectedDetailReasonIds().includes(reason.uuid),
    );
    const connector = this.selectedConnector();

    const intro = this.translateTextIntro(mainReason);

    if (!intro) {
      this.generatedText.set('');
      return;
    }

    const fragments = detailReasons
      .map((reason) => this.translateTextFragment(reason))
      .filter((text) => text.trim().length > 0);

    if (fragments.length === 0) {
      this.generatedText.set(intro);
      return;
    }

    const connectorPrimary = this.getConnectorPrimary(connector);
    const connectorSecondary = this.getConnectorSecondary(connector);

    const parts: string[] = [intro];

    fragments.forEach((fragment, index) => {
      const connectorText = index === 0 ? connectorPrimary : connectorSecondary;

      if (connectorText) {
        parts.push(connectorText);
      }

      parts.push(fragment);
    });

    const result = parts.join(' ').replace(/\s+/g, ' ').trim();
    this.generatedText.set(result);
  }

  private getConnectorPrimary(connector: RfxTextConnector | null): string {
    if (!connector?.translations?.length) {
      return '';
    }

    const activeLang = this.transloco.getActiveLang();
    const translation =
      connector.translations.find((t) => t.languages_code === activeLang) ??
      connector.translations[0];

    return translation?.connector_primary?.trim() ?? '';
  }

  private getConnectorSecondary(connector: RfxTextConnector | null): string {
    if (!connector?.translations?.length) {
      return '';
    }

    const activeLang = this.transloco.getActiveLang();
    const translation =
      connector.translations.find((t) => t.languages_code === activeLang) ??
      connector.translations[0];

    return translation?.connector_secondary?.trim() ?? '';
  }

  private translateMainReasonLabel(reason: RfxMainReason): string {
    const activeLang = this.transloco.getActiveLang();
    const translation =
      reason.text_intro?.find((t) => t.languages_code === activeLang) ?? reason.text_intro?.[0];

    return (
      translation?.label?.trim() ||
      translation?.description?.trim() ||
      translation?.text_intro?.trim() ||
      reason.uuid
    );
  }

  private translateDetailReasonLabel(reason: RfxDetailReason): string {
    const activeLang = this.transloco.getActiveLang();
    const translation =
      reason.text_fragment?.find((t) => t.languages_code === activeLang) ??
      reason.text_fragment?.[0];

    return (
      translation?.label?.trim() ||
      translation?.description?.trim() ||
      translation?.text_fragment?.trim() ||
      reason.uuid
    );
  }

  private translateTextIntro(reason: RfxMainReason | null | undefined): string {
    if (!reason) {
      return '';
    }

    const activeLang = this.transloco.getActiveLang();
    const translation =
      reason.text_intro?.find((t) => t.languages_code === activeLang) ?? reason.text_intro?.[0];

    return translation?.text_intro?.trim() ?? '';
  }

  private translateTextFragment(reason: RfxDetailReason): string {
    const activeLang = this.transloco.getActiveLang();
    const translation =
      reason.text_fragment?.find((t) => t.languages_code === activeLang) ??
      reason.text_fragment?.[0];

    return translation?.text_fragment?.trim() ?? '';
  }

  private resolveDecisionType(): 'participant' | 'rejected' {
    return this.tool()?.selection === TenderSelection.PARTICIPANT ? 'participant' : 'rejected';
  }

  private resolveRfxStage(): RfxStage {
    switch (this.phaseType()) {
      case TenderPhaseType.RFP:
        return 'rfp';
      case TenderPhaseType.RFD:
        return 'rfd';
      case TenderPhaseType.RFQ:
        return 'rfq';
      case TenderPhaseType.RFI:
      default:
        return 'rfi';
    }
  }

  protected async generate(): Promise<void> {
    if (!this.canGenerate()) {
      return;
    }

    this.loading.set(true);
    try {
      const response = await firstValueFrom(
        this.participatingToolService.generateParticipationReasons({
          projectId: this.projectId(),
          phase: this.phaseType(),
          participatingToolReasonRequest: {
            hrToolDirectusId: this.tool()!.id,
            mainReasonUuid: this.mainReasonId()!,
            detailReasonUuids: this.selectedDetailReasonIds() ?? [],
            reasonText: this.generatedText(),
            reasonComment: this.comment() || undefined,
          },
        }),
      );

      this.generatedText.set(this.resolveReasonTextFromResponse(response));
      this.canGenerateReasons.set(false);
    } finally {
      this.loading.set(false);
    }
  }

  private resolveReasonTextFromResponse(response: {
    rfpReasonText?: string;
    rfdReasonText?: string;
    rfqReasonText?: string;
  }): string {
    switch (this.phaseType()) {
      case TenderPhaseType.RFD:
        return response.rfdReasonText ?? this.generatedText();
      case TenderPhaseType.RFQ:
        return response.rfqReasonText ?? this.generatedText();
      case TenderPhaseType.RFP:
      default:
        return response.rfpReasonText ?? this.generatedText();
    }
  }

  protected async save(): Promise<void> {
    this.loading.set(true);
    try {
      await firstValueFrom(
        this.participatingToolService.updateParticipationReasons({
          projectId: this.projectId(),
          phase: this.phaseType(),
          participatingToolReasonRequest: {
            hrToolDirectusId: this.tool()!.id,
            mainReasonUuid: this.mainReasonId()!,
            detailReasonUuids: this.selectedDetailReasonIds() ?? [],
            reasonText: this.generatedText(),
            reasonComment: this.comment() || undefined,
          },
        }),
      );

      this.saved.emit({
        participatingToolId: this.tool()?.participatingToolId,
        mainReasonId: this.mainReasonId(),
        detailReasonIds: this.selectedDetailReasonIds(),
        generatedText: this.generatedText(),
        comment: this.comment(),
      });
      this.close();
    } finally {
      this.loading.set(false);
    }
  }
}
